import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../api/apiClient';
import { HiOutlineLockClosed } from 'react-icons/hi';
import logo from '../assets/logo_logo.svg';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
    } else {
      setError('Password reset token not found in the URL. Please request a new link.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError('Token is missing. Cannot proceed.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await resetPassword(token, password);
      setMessage(response.message || 'Your password has been reset successfully!');
    } catch (err) {
      setError(err.message || 'Failed to reset password. The token may be invalid or expired.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.18),_transparent_45%)]"></div>
        <div className="absolute -top-20 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-600/20 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-slate-700/30 blur-3xl"></div>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.85),rgba(2,6,23,0.98))]"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-slate-700/70 bg-slate-900/70 shadow-2xl shadow-blue-950/40 backdrop-blur">
            <div className="px-6 pt-8 pb-7 sm:px-8">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-700/80 bg-slate-800/80">
                  <img src={logo} alt="Hedger logo" className="h-9 w-9" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">Reset Your Password</h1>
                  <p className="text-sm text-slate-400">Enter your new password below.</p>
                </div>
              </div>

              {message ? (
                <div className="mt-7 text-center">
                  <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-200">
                    {message}
                  </div>
                  <Link
                    to="/login"
                    className="mt-5 inline-flex w-full justify-center rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-sky-500 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/30 transition-all hover:brightness-110"
                  >
                    Proceed to Sign In
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                  <div className="relative">
                    <HiOutlineLockClosed className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" />
                    <input
                      id="password" name="password" type="password" autoComplete="new-password" required
                      className="w-full rounded-xl border border-slate-700/80 bg-slate-800/70 py-3 pl-11 pr-4 text-white placeholder:text-slate-500 focus:border-blue-500/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      placeholder="New Password" value={password} onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <HiOutlineLockClosed className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" />
                    <input
                      id="confirm-password" name="confirm-password" type="password" autoComplete="new-password" required
                      className="w-full rounded-xl border border-slate-700/80 bg-slate-800/70 py-3 pl-11 pr-4 text-white placeholder:text-slate-500 focus:border-blue-500/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      placeholder="Confirm New Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  {error && (
                    <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !token}
                    className="w-full rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-sky-500 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/30 transition-all hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-blue-400/40 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              )}
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-slate-500">
            Choose a strong password to keep your account secure
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
